import moment from 'moment'
import { useMemo, useState } from 'react'

import { usePageTitle } from '../../hooks/usePageTitle'
import { POCKETSUITE_USER_ID } from '../../utils/constants'
import { PSApplication } from '../app/PSApplication'
import Card from '../app/design/Card'
import Heading from '../app/design/Heading'
import NavField, { FieldLink } from '../app/design/NavField'
import { SearchField } from '../common/fields/SearchField'

type Section = {
    title: string
    links: FieldLink[]
    disabled?: boolean
}

export const SettingsPage = () => {
    const user = PSApplication.me()
    const owner = PSApplication.owner()

    const [search, setSearch] = useState('')

    usePageTitle('Settings')

    const sections = useMemo(() => {
        const isSuperAdmin = user.role === 'owner'
        const isAdmin = user.role === 'admin'

        const sections: Section[] = [
            {
                title: 'Account',
                links: [
                    {
                        title: 'Features',
                        subtitle: 'Manage the features available on your account',
                        to: '/settings/account/features',
                        icon: 'form.large',
                        disabled: isAdmin,
                    },
                    {
                        title: 'Profile',
                        subtitle: 'Business logo, email, website, and description',
                        to: '/settings/account/profile',
                        icon: 'user2.large',
                    },
                    {
                        title: 'Location',
                        subtitle: 'Manage your business address',
                        to: '/settings/account/location',
                        icon: 'markerPin.large',
                    },
                    {
                        title: 'Default memos',
                        subtitle: 'Memos for invoices, charges, and appointments',
                        to: '/settings/account/memos',
                        icon: 'layers.large',
                        disabled: !isSuperAdmin,
                    },
                    {
                        title: 'Notifications',
                        subtitle: 'Remind clients of appointments or payments',
                        to: '/settings/account/notifications',
                        icon: 'bell.large',
                        disabled: !isSuperAdmin,
                    },

                    // TODO: these aren't referenced in Figma; reach out to product
                    // {
                    //     title: 'Number',
                    //     subtitle: '',
                    //     to: '/settings/account/number/edit',
                    //     icon: '',
                    //     disabled: !showNumber,
                    // },
                    // {
                    //     title: 'Number',
                    //     subtitle: '',
                    //     to: '/settings/account/numbers',
                    //     icon: '',
                    //     disabled: !showNumbers,
                    // },
                ],
            },
            {
                title: 'Services',
                links: [
                    {
                        title: 'Services',
                        subtitle: 'Manage the array of services offered to your clients',
                        to: '/settings/services',
                        icon: 'tag.large',
                    },
                    {
                        title: 'Packages',
                        subtitle: 'Manage packages of services and get paid upfront',
                        to: '/settings/packages',
                        icon: 'package.large',
                        disabled: !owner.allowPackage,
                    },
                    {
                        title: 'Subscriptions',
                        subtitle: 'Manage your subscriptions',
                        to: '/settings/subscriptions',
                        icon: 'refresh.large',
                        disabled: !owner.allowSubscription,
                    },
                    {
                        title: 'Classes',
                        subtitle: 'Setup, edit and schedule your classes',
                        to: '/settings/classes',
                        icon: 'student.large',
                        disabled: !owner.allowClass,
                    },
                    {
                        title: 'Reservations',
                        subtitle: 'Manage multi-day appointments',
                        to: '/settings/reservations',
                        icon: 'calendarClock.large',
                        disabled: !owner.allowReservation,
                    },
                    {
                        title: 'Categories',
                        subtitle:
                            'Organize your services into categories and make it easier for your client',
                        to: '/settings/categories',
                        icon: 'grid.large',
                        disabled: !owner.allowCategory,
                    },
                    {
                        title: owner.allowProduct ? 'Sort services & products' : 'Sort services',
                        subtitle: 'Rearrange the order in which your services appear online',
                        to: '/settings/sort',
                        icon: 'sort.large',
                    },
                    {
                        title: 'Products',
                        subtitle: 'Add and edit the products you offer clients',
                        to: '/settings/products',
                        icon: 'package.large',
                        disabled: !owner.allowProduct,
                    },
                    {
                        title: 'Gift certificates',
                        subtitle: 'Manage the gift certificates you offer',
                        to: '/settings/gifts',
                        icon: 'gift.large',
                        disabled: !owner.allowGift,
                    },
                ],
            },
            {
                title: 'Scheduling',
                links: [
                    {
                        title: 'Scheduling',
                        subtitle:
                            'Manage your availability, time between apointments and confirmation status',
                        to: '/settings/scheduling',
                        icon: 'windowClock.large',
                    },
                    {
                        title: 'Calendar Sync',
                        subtitle: 'Sync your personal calendar and avoid double-booking',
                        to: '/settings/calendar/sync',
                        icon: 'calendarRefresh.large',
                    },
                    // TODO
                    // {
                    //     title: 'Product orders',
                    //     subtitle: 'Setup product orders for pickup or shipment',
                    //     to: '',
                    //     icon: <Truck />,
                    // },
                    {
                        title: 'Cancellations',
                        subtitle: 'Protect yourself from no-shows and cancellations',
                        to: '/settings/cancellations',
                        icon: 'calendarMinus.large',
                        disabled: isAdmin,
                    },
                    {
                        title: 'Video conference',
                        subtitle: 'Select your default video conferencing options',
                        to: '/settings/videoconferencing',
                        icon: 'video.large',
                        disabled: !owner.plan || isAdmin,
                    },
                    // TODO
                    // {
                    //     title: 'Resources',
                    //     subtitle: 'Manage the use of space & equipment with your bookings',
                    //     to: '',
                    //     icon: <Tool />,
                    // },
                    // {
                    //     title: 'Resource groups',
                    //     subtitle: 'Add resources to groups so they can be assigned to bookings',
                    //     to: '',
                    //     icon: <Tools />,
                    // },
                ],
                disabled: !owner.allowBooking,
            },
            {
                title: 'Marketing Suite',
                links: [
                    // TODO
                    // {
                    //     title: 'Online booking',
                    //     subtitle: 'Manage your online booking site',
                    //     to: '',
                    //     icon: <ComputerClock />,
                    // },
                    // TODO
                    // {
                    //     title: 'Client fields',
                    //     subtitle: 'Add and edit your fields for clients',
                    //     to: '',
                    //     icon: <Form />,
                    // },
                    // TODO
                    // {
                    //     title: 'Leads & reviews',
                    //     subtitle: 'Request reviews and get new leads through Google Reserve',
                    //     to: '',
                    //     icon: <Google />, // FIXME: something is off about this SVG
                    // },
                    {
                        title: 'Suite link',
                        subtitle: 'Edit your business links',
                        to: '/settings/link',
                        icon: 'monitor.large',
                        disabled: !owner.allowLink,
                    },
                    {
                        title: 'Online chat',
                        subtitle: 'Setup and preview your online chat popup',
                        to: '/settings/chat',
                        icon: 'computerMessage.large',
                        disabled: !owner.allowChat,
                    },
                    {
                        title: 'Online leads',
                        subtitle: "Manage the info you're collecting from new leads",
                        to: '/settings/leads',
                        icon: 'computerCheckmark.large',
                        disabled: !owner.allowLead,
                    },
                    // TODO
                    // {
                    //     title: 'Online ordering',
                    //     subtitle: 'Setup your online ordering site',
                    //     to: '',
                    //     icon: <Parcel />,
                    // },
                    {
                        title: 'Direct payments',
                        subtitle: 'Edit your direct payments site',
                        to: '/settings/payments',
                        icon: 'computerDollar.large',
                        disabled: !owner.allowPaypal,
                    },
                    // TODO
                    // {
                    //     title: 'Saved messages',
                    //     subtitle:
                    //         'Setup & manage saved messages and stop writing the same texts over and over',
                    //     to: '',
                    //     icon: <MessageCheck />,
                    // },
                    {
                        title: 'Contracts',
                        subtitle: 'Add and edit contracts to protect your business',
                        to: '/settings/contracts',
                        icon: 'pen.large',
                        disabled: !owner.allowContract,
                    },
                    {
                        title: 'Forms',
                        subtitle:
                            "Manage the questions you're asking and information you're collecting",
                        to: '/settings/forms',
                        icon: 'field.large',
                        disabled: !owner.allowRecord,
                    },
                ],
                disabled:
                    isAdmin ||
                    !(
                        owner.allowContract ||
                        owner.allowRecord ||
                        owner.allowLead ||
                        owner.allowChat ||
                        owner.allowPayment ||
                        owner.allowLink
                    ),
            },
            {
                title: 'Payments',
                links: [
                    {
                        title: 'Payment methods',
                        subtitle: 'Manage the accounts you have on file',
                        to: '/settings/payments/methods',
                        icon: 'cards.large',
                    },
                    {
                        title: 'Payouts',
                        subtitle: 'Receive payouts in as little as 15 minutes',
                        to: '/settings/payments/payout',
                        icon: 'calendarDollar.large',
                    },
                    {
                        title: 'Card readers',
                        subtitle: 'Order a new card reader or set up a new device',
                        to: '/settings/reader',
                        icon: 'cardReader.large',
                        disabled: !owner.allowReader,
                    },
                    {
                        title: 'Gratuity',
                        subtitle:
                            "Select when and how you'd like clients to be prompted to add a tip",
                        to: '/settings/payments/gratuity',
                        icon: 'money.large',
                        disabled: !owner.allowGratuity,
                    },
                    {
                        title: 'Sales tax',
                        subtitle:
                            "Add your business address and we'll automatically calculate your tax rate",
                        to: '/settings/payments/sales-tax',
                        icon: 'percentage2.large',
                        disabled: !owner.allowSalesTax,
                    },
                ],
            },
            {
                title: 'Support',
                links: [
                    {
                        title: 'Change plan',
                        subtitle:
                            'View our range of plans and find the one that fits your business',
                        to: '/settings/change-plan',
                        icon: 'folderPen.large',
                        disabled: isAdmin,
                    },
                    {
                        title: 'Tutorials',
                        subtitle: 'Check out video tutorials on each of our features',
                        to: '/settings/tutorials',
                        icon: 'cap.large',
                    },
                    // TODO
                    // {
                    //     title: 'FAQs',
                    //     subtitle: 'Visit our Suite help center for more in depth walk-throughs',
                    //     to: '',
                    //     icon: <Help />,
                    // },
                    {
                        title: 'Contact us',
                        subtitle: 'Text PocketSuite support',
                        to: `/messages/${POCKETSUITE_USER_ID}`,
                        icon: 'message.large',
                    },
                    {
                        title: 'Request a call',
                        subtitle: 'Request ASAP callback, schedule call or access video tutorials',
                        to: '/settings/request-call',
                        icon: 'phone.large',
                        disabled: !user.allowPrioritySupport,
                    },
                    {
                        title: 'Send tax form',
                        subtitle: 'Export your 1099K tax form last year',
                        to: '/settings/tax-form',
                        icon: 'envelope.large',
                        disabled:
                            !isSuperAdmin ||
                            !owner.firstPaymentDate ||
                            moment(owner.firstPaymentDate).year() === moment().year(),
                    },
                    {
                        title: 'Data export',
                        subtitle: 'Export your payment, appointment, client and other data',
                        to: '/settings/data-export',
                        icon: 'phoneArrowUp.large',
                        disabled: !isSuperAdmin,
                    },
                    // TODO
                    // {
                    //     title: 'Data import',
                    //     subtitle: 'Import your clients, services and products',
                    //     to: '',
                    //     icon: <PhoneArrowDown />,
                    // },
                    {
                        title: 'Privacy & terms',
                        subtitle: 'Review our privacy policy and terms of service',
                        to: `/settings/privacy-terms`,
                        icon: 'documentLock.large',
                    },
                ],
            },
        ]

        return sections
    }, [user, owner])

    return (
        <div className="max-w-large mx-auto flex flex-col gap-6 py-8">
            <div className="flex items-center justify-between px-8">
                <Heading size="md">Settings</Heading>

                <div className="w-[240px]">
                    <SearchField
                        placeholder="Search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onClear={() => setSearch('')}
                        isFullWidth
                        autoFocus
                    />
                </div>
            </div>

            {sections
                .filter((s) => !s.disabled)
                .map((section) => {
                    const links = section.links
                        .filter((l) => !l.disabled)
                        .filter((l) => l.title.toLowerCase().includes(search.toLowerCase()))
                        .map((link) => <NavField key={link.title} link={link} />)

                    return (
                        links.length > 0 && (
                            <Card key={section.title} className="pb-4">
                                <Heading size="sm" className="mb-4">
                                    {section.title}
                                </Heading>

                                {links}
                            </Card>
                        )
                    )
                })}
        </div>
    )
}
